package com.zq.webproject.ui.upload

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.zq.webproject.network.Repository
import com.zq.webproject.utils.TokenManager
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.Font
import webproject.composeapp.generated.resources.Res
import webproject.composeapp.generated.resources.font_song

// 统一字体
private val CaiFont: FontFamily
    @Composable
    get() = FontFamily(Font(Res.font.font_song))

/**
 * 上传商品界面
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun UploadProductScreen(
    onBackClick: () -> Unit
) {
    val repository = remember { Repository() }
    val scope = rememberCoroutineScope()
    
    // 表单状态
    var name by remember { mutableStateOf("") }
    var linkUrl by remember { mutableStateOf("") }
    var category by remember { mutableStateOf("") }
    var remark by remember { mutableStateOf("") }
    
    // 验证状态
    var nameError by remember { mutableStateOf(false) }
    var linkUrlError by remember { mutableStateOf(false) }
    var categoryError by remember { mutableStateOf(false) }
    
    // 提交状态
    var isSubmitting by remember { mutableStateOf(false) }
    var showSuccessDialog by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    
    // 分类下拉菜单状态
    var categoryExpanded by remember { mutableStateOf(false) }
    val categoryOptions = listOf(
        "视频教程",
        "文档教程",
        "软件工具",
        "素材资源",
        "其他"
    )

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFFF5F5F5))
            .systemBarsPadding()
    ) {
        // 顶部标题栏
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .background(Color.White)
                .padding(vertical = 16.dp, horizontal = 16.dp)
        ) {
            // 返回按钮
            IconButton(
                onClick = onBackClick,
                modifier = Modifier.align(Alignment.CenterStart)
            ) {
                Icon(
                    imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                    contentDescription = "返回",
                    tint = Color(0xFF333333)
                )
            }
            
            // 标题
            Text(
                text = "上传商品",
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF333333),
                fontFamily = CaiFont,
                modifier = Modifier.align(Alignment.Center)
            )
        }

        // 表单内容区域（可滚动）
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(16.dp)
        ) {
            Spacer(modifier = Modifier.height(8.dp))

            // 错误提示
            if (errorMessage != null) {
                Card(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 16.dp),
                    colors = CardDefaults.cardColors(
                        containerColor = Color(0xFFFFEBEE)
                    ),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = "❌ $errorMessage",
                            color = Color(0xFFD32F2F),
                            fontSize = 14.sp,
                            fontFamily = CaiFont
                        )
                    }
                }
            }

            // 商品名称
            Text(
                text = "商品名称",
                fontSize = 14.sp,
                fontWeight = FontWeight.Medium,
                color = Color(0xFF333333),
                fontFamily = CaiFont,
                modifier = Modifier.padding(bottom = 8.dp)
            )
            OutlinedTextField(
                value = name,
                onValueChange = {
                    name = it
                    nameError = false
                    errorMessage = null
                },
                modifier = Modifier.fillMaxWidth(),
                placeholder = {
                    Text(
                        text = "请输入商品名称",
                        fontFamily = CaiFont,
                        color = Color.Gray
                    )
                },
                isError = nameError,
                supportingText = if (nameError) {
                    { Text("商品名称不能为空", fontFamily = CaiFont) }
                } else null,
                shape = RoundedCornerShape(8.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = Color(0xFF1E88E5),
                    unfocusedBorderColor = Color(0xFFE0E0E0)
                ),
                singleLine = true,
                enabled = !isSubmitting
            )

            Spacer(modifier = Modifier.height(16.dp))

            // 链接地址
            Text(
                text = "链接地址",
                fontSize = 14.sp,
                fontWeight = FontWeight.Medium,
                color = Color(0xFF333333),
                fontFamily = CaiFont,
                modifier = Modifier.padding(bottom = 8.dp)
            )
            OutlinedTextField(
                value = linkUrl,
                onValueChange = {
                    linkUrl = it
                    linkUrlError = false
                    errorMessage = null
                },
                modifier = Modifier.fillMaxWidth(),
                placeholder = {
                    Text(
                        text = "请输入资源链接地址",
                        fontFamily = CaiFont,
                        color = Color.Gray
                    )
                },
                isError = linkUrlError,
                supportingText = if (linkUrlError) {
                    { Text("链接地址不能为空", fontFamily = CaiFont) }
                } else null,
                shape = RoundedCornerShape(8.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = Color(0xFF1E88E5),
                    unfocusedBorderColor = Color(0xFFE0E0E0)
                ),
                singleLine = true,
                enabled = !isSubmitting
            )

            Spacer(modifier = Modifier.height(16.dp))

            // 商品分类
            Text(
                text = "商品分类",
                fontSize = 14.sp,
                fontWeight = FontWeight.Medium,
                color = Color(0xFF333333),
                fontFamily = CaiFont,
                modifier = Modifier.padding(bottom = 8.dp)
            )
            ExposedDropdownMenuBox(
                expanded = categoryExpanded,
                onExpandedChange = { 
                    if (!isSubmitting) {
                        categoryExpanded = !categoryExpanded
                    }
                }
            ) {
                OutlinedTextField(
                    value = category,
                    onValueChange = {},
                    readOnly = true,
                    modifier = Modifier
                        .fillMaxWidth()
                        .menuAnchor(),
                    placeholder = {
                        Text(
                            text = "请选择商品分类",
                            fontFamily = CaiFont,
                            color = Color.Gray
                        )
                    },
                    trailingIcon = {
                        ExposedDropdownMenuDefaults.TrailingIcon(expanded = categoryExpanded)
                    },
                    isError = categoryError,
                    supportingText = if (categoryError) {
                        { Text("请选择商品分类", fontFamily = CaiFont) }
                    } else null,
                    shape = RoundedCornerShape(8.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = Color(0xFF1E88E5),
                        unfocusedBorderColor = Color(0xFFE0E0E0)
                    ),
                    enabled = !isSubmitting
                )
                
                ExposedDropdownMenu(
                    expanded = categoryExpanded,
                    onDismissRequest = { categoryExpanded = false }
                ) {
                    categoryOptions.forEach { option ->
                        DropdownMenuItem(
                            text = {
                                Text(
                                    text = option,
                                    fontFamily = CaiFont
                                )
                            },
                            onClick = {
                                category = option
                                categoryExpanded = false
                                categoryError = false
                                errorMessage = null
                            }
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            // 备注信息
            Text(
                text = "备注信息",
                fontSize = 14.sp,
                fontWeight = FontWeight.Medium,
                color = Color(0xFF333333),
                fontFamily = CaiFont,
                modifier = Modifier.padding(bottom = 8.dp)
            )
            OutlinedTextField(
                value = remark,
                onValueChange = { remark = it },
                modifier = Modifier
                    .fillMaxWidth()
                    .height(120.dp),
                placeholder = {
                    Text(
                        text = "请输入备注信息（选填）",
                        fontFamily = CaiFont,
                        color = Color.Gray
                    )
                },
                shape = RoundedCornerShape(8.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = Color(0xFF1E88E5),
                    unfocusedBorderColor = Color(0xFFE0E0E0)
                ),
                maxLines = 5,
                enabled = !isSubmitting
            )

            Spacer(modifier = Modifier.height(32.dp))

            // 提交按钮
            Button(
                onClick = {
                    // 验证表单
                    var hasError = false
                    if (name.isBlank()) {
                        nameError = true
                        hasError = true
                    }
                    if (linkUrl.isBlank()) {
                        linkUrlError = true
                        hasError = true
                    }
                    if (category.isBlank()) {
                        categoryError = true
                        hasError = true
                    }
                    
                    if (!hasError) {
                        // 调用API上传商品
                        isSubmitting = true
                        errorMessage = null
                        
                        scope.launch {
                            try {
                                val token = TokenManager.getToken()
                                if (token == null) {
                                    errorMessage = "未登录，请先登录"
                                    isSubmitting = false
                                    return@launch
                                }
                                
                                println("UploadProductScreen - 开始上传商品")
                                val result = repository.uploadProduct(
                                    name = name,
                                    linkUrl = linkUrl,
                                    category = category,
                                    remark = remark.ifBlank { null },
                                    token = token
                                )
                                
                                result.onSuccess { response ->
                                    if (response.code == 200) {
                                        println("UploadProductScreen - 上传成功，商品ID: ${response.data?.productId}")
                                        isSubmitting = false
                                        showSuccessDialog = true
                                        // 清空表单
                                        name = ""
                                        linkUrl = ""
                                        category = ""
                                        remark = ""
                                    } else {
                                        errorMessage = response.message ?: "上传失败"
                                        isSubmitting = false
                                    }
                                }.onFailure { error ->
                                    println("UploadProductScreen - 上传失败: ${error.message}")
                                    errorMessage = "上传失败: ${error.message}"
                                    isSubmitting = false
                                }
                            } catch (e: Exception) {
                                println("UploadProductScreen - 上传异常: ${e.message}")
                                errorMessage = "上传异常: ${e.message}"
                                isSubmitting = false
                            }
                        }
                    }
                },
                modifier = Modifier
                    .fillMaxWidth()
                    .height(50.dp),
                enabled = !isSubmitting,
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color(0xFF1E88E5)
                ),
                shape = RoundedCornerShape(12.dp)
            ) {
                if (isSubmitting) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(24.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = "上传中...",
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        fontFamily = CaiFont
                    )
                } else {
                    Text(
                        text = "提交上传",
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        fontFamily = CaiFont
                    )
                }
            }

            // 底部安全区域适配
            Spacer(
                modifier = Modifier
                    .fillMaxWidth()
                    .windowInsetsBottomHeight(WindowInsets.navigationBars)
            )
            
            Spacer(modifier = Modifier.height(16.dp))
        }
    }
    
    // 成功提示对话框
    if (showSuccessDialog) {
        AlertDialog(
            onDismissRequest = { },
            title = {
                Row(
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        imageVector = Icons.Default.CheckCircle,
                        contentDescription = null,
                        tint = Color(0xFF4CAF50),
                        modifier = Modifier.size(32.dp)
                    )
                    Spacer(modifier = Modifier.width(12.dp))
                    Text(
                        text = "上传成功",
                        fontFamily = CaiFont,
                        fontWeight = FontWeight.Bold
                    )
                }
            },
            text = {
                Text(
                    text = "商品已成功上传，将在首页展示！",
                    fontFamily = CaiFont
                )
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        showSuccessDialog = false
                        onBackClick()
                    }
                ) {
                    Text(
                        text = "确定",
                        fontFamily = CaiFont,
                        color = Color(0xFF1E88E5)
                    )
                }
            }
        )
    }
}
